import { isValidCpf } from "./validateCpf";

export const CNPJ_PATTERN = /^[0-9A-Z]{12}\d{2}$/;

const DV1_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const DV2_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

export function cleanDocument(value?: string | null): string {
  return value?.replace(/[^0-9A-Za-z]/g, "").toUpperCase() ?? "";
}

export function hasCnpjLetters(value?: string | null): boolean {
  return /[A-Za-z]/.test(cleanDocument(value));
}

export function hasCnpjShape(value?: string | null): boolean {
  return CNPJ_PATTERN.test(cleanDocument(value));
}

function charValue(char: string): number {
  return char.charCodeAt(0) - 48;
}

function calculateCheckDigit(chars: string, weights: number[]): number {
  const sum = weights.reduce(
    (accumulator, weight, index) => accumulator + charValue(chars[index]) * weight,
    0
  );
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

export function isValidCnpj(value?: string | null): boolean {
  const document = cleanDocument(value);
  if (!CNPJ_PATTERN.test(document)) return false;
  if (/^(.)\1{13}$/.test(document)) return false;
  const firstCheckDigit = calculateCheckDigit(document.slice(0, 12), DV1_WEIGHTS);
  const secondCheckDigit = calculateCheckDigit(document.slice(0, 13), DV2_WEIGHTS);
  return (
    document[12] === String(firstCheckDigit) &&
    document[13] === String(secondCheckDigit)
  );
}

export function isAlphanumericCnpj(value?: string | null): boolean {
  const document = cleanDocument(value);
  return hasCnpjLetters(document) && hasCnpjShape(document) && isValidCnpj(document);
}

export function isValidCpfOrCnpj(value?: string | null): boolean {
  const document = cleanDocument(value);
  if (hasCnpjLetters(document) || document.length === 14) {
    return isValidCnpj(document);
  }
  if (document.length === 11) {
    return isValidCpf(document);
  }
  return false;
}

export function detectDocumentType(value?: string | null): "CPF" | "CNPJ" | undefined {
  const document = cleanDocument(value);
  if (hasCnpjLetters(document) || document.length === 14) return "CNPJ";
  if (document.length === 11) return "CPF";
  return undefined;
}

export function maskCnpjChars(value?: string | null): string {
  const document = cleanDocument(value).slice(0, 14);
  return document.slice(0, 12) + document.slice(12, 14).replace(/\D/g, "");
}

export function formatCnpjString(value?: string | null): string {
  return maskCnpjChars(value)
    .replace(/^([0-9A-Z]{2})([0-9A-Z])/, "$1.$2")
    .replace(/^([0-9A-Z]{2}\.[0-9A-Z]{3})([0-9A-Z])/, "$1.$2")
    .replace(/^([0-9A-Z]{2}\.[0-9A-Z]{3}\.[0-9A-Z]{3})([0-9A-Z])/, "$1/$2")
    .replace(/^([0-9A-Z]{2}\.[0-9A-Z]{3}\.[0-9A-Z]{3}\/[0-9A-Z]{4})(\d{1,2})/, "$1-$2");
}
